/**
 * Host-side plugins (DL-PLUGIN).
 *
 * A plugin has two layers:
 *
 * 1. **Model definitions** (`types`): serializable entity types evaluated by the
 *    Rust engine (expressions, anchors, primitives, constraint templates,
 *    migrations). No JavaScript runs inside the solver.
 * 2. **Host code**: trusted TypeScript that turns interaction into commands —
 *    tools, named commands, snap providers, panels, importers/exporters and
 *    storage adapters.
 *
 * Lifecycle: `register` → enabled (default) ⇄ `disable`/`enable` → `unregister`.
 * Disabling keeps documents intact: the engine shows the plugin's entities
 * read-only from their stored fallback until the plugin is enabled again.
 */
import { DotloomError } from './protocol.js';
import { SDK_VERSION, satisfies } from './version.js';
const ID_RE = /^[a-z][a-z0-9-]*(\.[a-z0-9-]+)+$/;
export class PluginHost {
    engine;
    editor;
    entries = new Map();
    listeners = new Set();
    constructor(engine, editor = null) {
        this.engine = engine;
        this.editor = editor;
    }
    /** Subscribe to registry changes. */
    subscribe(cb) {
        this.listeners.add(cb);
        return () => this.listeners.delete(cb);
    }
    changed() {
        for (const cb of [...this.listeners])
            cb();
    }
    api(entry) {
        return {
            engine: this.engine,
            editor: this.editor,
            pluginId: entry.plugin.id,
            on: ((name, cb) => {
                const off = this.engine.on(name, cb);
                entry.cleanups.push(off);
                return off;
            }),
            onDispose: (fn) => entry.cleanups.push(fn),
        };
    }
    /** Register (and by default enable) a plugin. Errors leave nothing registered. */
    async register(plugin, opts = {}) {
        if (!ID_RE.test(plugin.id)) {
            throw new DotloomError({
                code: 'plugin',
                message: `invalid plugin id "${plugin.id}" (use a namespace like acme.shelves)`,
            });
        }
        if (this.entries.has(plugin.id)) {
            throw new DotloomError({ code: 'plugin', message: `plugin "${plugin.id}" is already registered` });
        }
        if (plugin.sdk && !satisfies(SDK_VERSION, plugin.sdk)) {
            throw new DotloomError({
                code: 'plugin',
                message: `plugin "${plugin.id}" requires @dotloomjs/sdk ${plugin.sdk}, this is ${SDK_VERSION}`,
            });
        }
        for (const t of plugin.types ?? []) {
            if (!t.typeId.startsWith(`${plugin.id}.`) && !t.typeId.startsWith(`${plugin.id.split('.')[0]}.`)) {
                throw new DotloomError({
                    code: 'plugin',
                    message: `type "${t.typeId}" must use the plugin namespace "${plugin.id.split('.')[0]}."`,
                });
            }
        }
        const entry = { plugin, enabled: false, cleanups: [], tools: [] };
        if (plugin.types?.length) {
            // The engine validates definitions and reports conflicts as typed errors.
            await this.engine.registerTypes(plugin.types, plugin.id);
        }
        this.entries.set(plugin.id, entry);
        if (opts.enabled === false) {
            for (const t of plugin.types ?? [])
                await this.engine.setTypeEnabled(t.typeId, false);
        }
        else {
            try {
                await this.activate(entry);
            }
            catch (e) {
                await this.unregister(plugin.id).catch(() => undefined);
                throw e;
            }
        }
        this.changed();
    }
    async activate(entry) {
        const p = entry.plugin;
        for (const t of p.types ?? [])
            await this.engine.setTypeEnabled(t.typeId, true);
        if (this.editor) {
            for (const make of p.tools ?? []) {
                try {
                    const tool = make();
                    entry.cleanups.push(this.editor.registerTool(tool));
                    entry.tools.push(tool.id);
                }
                catch (e) {
                    this.runCleanups(entry);
                    throw e;
                }
            }
            for (const sp of p.snapProviders ?? [])
                entry.cleanups.push(this.editor.addSnapProvider(sp));
        }
        const cleanup = p.activate?.(this.api(entry));
        if (typeof cleanup === 'function')
            entry.cleanups.push(cleanup);
        entry.enabled = true;
    }
    runCleanups(entry) {
        entry.tools = [];
        for (const f of entry.cleanups.splice(0).reverse()) {
            try {
                f();
            }
            catch {
                // a failing cleanup must not block the others
            }
        }
    }
    /** Disable: tools/commands disappear, the plugin's entities become read-only (data kept). */
    async disable(id) {
        const e = this.get(id);
        if (!e.enabled)
            return;
        this.runCleanups(e);
        for (const t of e.plugin.types ?? [])
            await this.engine.setTypeEnabled(t.typeId, false);
        e.enabled = false;
        this.changed();
    }
    async enable(id) {
        const e = this.get(id);
        if (e.enabled)
            return;
        await this.activate(e);
        this.changed();
    }
    /** Remove the plugin. Documents keep its entities (read-only until registered again). */
    async unregister(id) {
        const e = this.entries.get(id);
        if (!e)
            return;
        this.runCleanups(e);
        for (const t of e.plugin.types ?? [])
            await this.engine.unregisterType(t.typeId).catch(() => undefined);
        this.entries.delete(id);
        this.changed();
    }
    get(id) {
        const e = this.entries.get(id);
        if (!e)
            throw new DotloomError({ code: 'notFound', message: `plugin "${id}" is not registered` });
        return e;
    }
    list() {
        return [...this.entries.values()].map((e) => ({
            id: e.plugin.id,
            version: e.plugin.version,
            enabled: e.enabled,
            types: (e.plugin.types ?? []).map((t) => t.typeId),
            tools: [...e.tools],
            commands: Object.keys(e.plugin.commands ?? {}),
        }));
    }
    /** Run a named plugin command (`pluginId:command`) as one transaction. */
    async run(qualified, args) {
        const [pid, name] = qualified.split(':');
        const e = this.get(pid ?? '');
        if (!e.enabled)
            throw new DotloomError({ code: 'plugin', message: `plugin "${pid}" is disabled` });
        const cmd = name ? e.plugin.commands?.[name] : undefined;
        if (!cmd)
            throw new DotloomError({ code: 'notFound', message: `unknown command "${qualified}"` });
        const tx = await cmd.run(this.api(e), args);
        return this.editor ? this.editor.apply(tx) : this.engine.apply(tx);
    }
    /** Importer for a file name (enabled plugins only). */
    importerFor(fileName) {
        const lower = fileName.toLowerCase();
        for (const e of this.entries.values()) {
            if (!e.enabled)
                continue;
            for (const imp of e.plugin.importers ?? []) {
                if (imp.extensions.some((x) => lower.endsWith(x)))
                    return { plugin: e.plugin.id, importer: imp };
            }
        }
        return null;
    }
    /** Run an importer as one undoable transaction. */
    async importFile(fileName, bytes) {
        const found = this.importerFor(fileName);
        if (!found)
            throw new DotloomError({ code: 'import', message: `no importer for ${fileName}` });
        const e = this.get(found.plugin);
        const tx = await found.importer.import(bytes, this.api(e));
        return this.editor ? this.editor.apply(tx) : this.engine.apply(tx);
    }
    exporters() {
        return [...this.entries.values()]
            .filter((e) => e.enabled)
            .flatMap((e) => (e.plugin.exporters ?? []).map((exporter) => ({ plugin: e.plugin.id, exporter })));
    }
    async exportWith(plugin, exporterId) {
        const e = this.get(plugin);
        const ex = e.plugin.exporters?.find((x) => x.id === exporterId);
        if (!ex || !e.enabled)
            throw new DotloomError({ code: 'notFound', message: `unknown exporter ${plugin}:${exporterId}` });
        return ex.export(this.api(e));
    }
    panels() {
        return [...this.entries.values()]
            .filter((e) => e.enabled)
            .flatMap((e) => (e.plugin.panels ?? []).map((panel) => ({ plugin: e.plugin.id, panel })));
    }
    constraintTemplates() {
        return [...this.entries.values()]
            .filter((e) => e.enabled)
            .flatMap((e) => (e.plugin.constraintTemplates ?? []).map((template) => ({ plugin: e.plugin.id, template })));
    }
    /** Storage adapters contributed by enabled plugins, in registration order. */
    storageAdapters() {
        return [...this.entries.values()].filter((e) => e.enabled).flatMap((e) => e.plugin.storage ?? []);
    }
    /**
     * Mount a plugin panel with an API that tracks the selection. The returned
     * unmount function is idempotent; disabling or unregistering the plugin also
     * unmounts it.
     */
    mountPanel(plugin, panelId, el) {
        const e = this.get(plugin);
        if (!e.enabled)
            throw new DotloomError({ code: 'plugin', message: `plugin "${plugin}" is disabled` });
        const panel = e.plugin.panels?.find((p) => p.id === panelId);
        if (!panel)
            throw new DotloomError({ code: 'notFound', message: `unknown panel ${plugin}:${panelId}` });
        const unmount = panel.mount(el, {
            ...this.api(e),
            selection: () => this.editor?.state.getSnapshot().selection ?? [],
        });
        let done = false;
        const off = () => {
            if (done)
                return;
            done = true;
            const i = e.cleanups.indexOf(off);
            if (i >= 0)
                e.cleanups.splice(i, 1);
            unmount();
        };
        e.cleanups.push(off);
        return off;
    }
    /** Unregister everything. */
    async dispose() {
        for (const id of [...this.entries.keys()])
            await this.unregister(id);
        this.listeners.clear();
    }
}
//# sourceMappingURL=plugins.js.map